import { bungie } from '../lib/bungie';
import { idbGet, idbSet } from '../lib/idb';
import { definition, items, tablesOf } from '../lib/manifest';
import type { Player } from './hub';
import { godRolls } from './godrolls';

/**
 * Meta weapons and Meta Builds from real match reports (port of the app's MetaSampler): starts from the player's own
 * matches and spreads to the players in them, keeps 14 days of matches, counts weapon usage and the equipped loadouts.
 * Runs in the browser; the matches and the results are kept in IndexedDB.
 */

const PARALLEL = 12;
const SIDE_MODES: Record<Side, number[]> = { pvp: [5], pve: [4, 82, 46] };   // AllPvP | raids, dungeons, Nightfalls
const TARGET: Record<Side, number> = { pvp: 150, pve: 100 };
const BUCKET_SLOTS: [number, string][] = [[3448274439, 'Helmet'], [3551918588, 'Gauntlets'], [14239492, 'Chest'], [20886954, 'Legs'], [1585787867, 'Class item']];
const WEAPON_BUCKETS = [1498876634, 2465295065, 953998645];
const SUB_BUCKET = 3284755031;
const POWER_STAT = '1935470627';
export type Side = 'pvp' | 'pve';

interface Match { s: Side; t: string; d: number; p: [number, number][][]; i: string[] }   // side, tag, when, each player's [weapon, kills], "type:mid:cid"
type Store = Record<string, Match>;

export interface MetaWeapon { hash: number; usage: number; kills: number; trend: number | null; godRoll: { name: string; icon: string }[]; frame: string }
export interface MetaResult { updated: number; sides: Record<Side, { matches: number; players: number; top: MetaWeapon[] }> }
export interface Plug { hash: number; name: string; icon: string; type: string; slot?: string; exotic?: boolean }
export interface Build {
  side: Side; cls: string; purpose: string; count: number; share: number; subclass: Plug | null; super: Plug[]; abilities: Plug[]; aspects: Plug[]; fragments: Plug[];
  armor: Plug[]; sets: { name: string; pieces: number; bonuses: { count: number; name: string; desc: string }[] }[]; mods: Plug[]; stats: { name: string; value: number }[]; weapons: Plug[];
}
export interface BuildsResult { updated: number; players: number; builds: Build[] }

export const readMeta = () => idbGet<MetaResult>('meta:result');
export const readBuilds = () => idbGet<BuildsResult>('meta:builds');

async function pool<T>(list: T[], work: (t: T) => Promise<void>, stop: () => boolean) {
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(PARALLEL, list.length) }, async () => {
    while (next < list.length && !stop()) { const t = list[next++]; try { await work(t); } catch { /* one failed report or profile is skipped */ } }
  }));
}

/** Recent instance ids (after the cutoff) from each player's most recently played character. Players are "type:mid". */
async function histories(players: string[], modes: number[], count: number, cutoff: number, stop: () => boolean) {
  const ids = new Set<string>();
  await pool(players, async (pl) => {
    const [type, mid] = pl.split(':');
    const chars = (await bungie<any>(`/Destiny2/${type}/Profile/${mid}/?components=200`)).characters?.data ?? {};
    let best: string | undefined, bestT = 0;
    for (const [cid, c] of Object.entries<any>(chars)) { const t = Date.parse(c.dateLastPlayed); if (t > bestT) { bestT = t; best = cid; } }
    if (!best) return;
    for (const mode of modes) {
      try {
        const h = await bungie<any>(`/Destiny2/${type}/Account/${mid}/Character/${best}/Stats/Activities/?mode=${mode}&count=${count}&page=0`);
        for (const a of h.activities ?? []) if (Date.parse(a.period) >= cutoff) ids.add(a.activityDetails?.instanceId);
      } catch { /* private or empty history */ }
    }
  }, stop);
  ids.delete(undefined as any);
  return [...ids];
}

function tagOf(side: Side, modes: number[]) {
  return side === 'pvp' ? (modes.includes(84) ? 'Trials' : modes.includes(19) ? 'Iron Banner' : 'Crucible')
    : modes.includes(4) ? 'Raid' : modes.includes(82) ? 'Dungeon' : modes.includes(46) ? 'Nightfall / GM' : 'PvE';
}

/** Collects matches until each side has enough (or 5 minutes pass), counts the meta, then builds Meta Builds. */
export async function runMeta(me: Player, progress: (text: string) => void, stop: () => boolean) {
  const cutoff = Date.now() - 14 * 86400e3;
  const store: Store = Object.fromEntries(Object.entries((await idbGet<Store>('meta:store')) ?? {}).filter(([, m]) => m.d >= cutoff));
  const started = Date.now();
  const meKey = `${me.membershipType}:${me.membershipId}`;
  const seen = new Set([meKey]);
  const met: string[] = [];
  const timeUp = () => stop() || Date.now() - started > 5 * 60e3;
  for (const side of ['pvp', 'pve'] as Side[]) {
    const need = TARGET[side] - Object.values(store).filter((m) => m.s === side && m.i.length).length;
    if (need <= 0) continue;
    progress(`Finding recent ${side.toUpperCase()} matches...`);
    const queue = await histories([meKey, ...(side === 'pve' ? met.slice(0, 80) : [])], SIDE_MODES[side], 20, cutoff, timeUp);
    let got = 0, lastSave = 0;
    while (queue.length && got < need && !timeUp()) {
      const batch = queue.splice(0, 40).filter((id) => !store[id]?.i.length);
      if (!batch.length) continue;
      const fresh: string[] = [];
      await pool(batch, async (id) => {
        const r = await bungie<any>(`/Destiny2/Stats/PostGameCarnageReport/${id}/`, { stats: true });
        const modes: number[] = r.activityDetails?.modes ?? [];
        const when = Date.parse(r.period);
        if ((side === 'pvp') !== modes.includes(5) || when < cutoff) return;
        const m: Match = { s: side, t: tagOf(side, modes), d: when, p: [], i: [] };
        for (const e of r.entries ?? []) {
          m.p.push((e.extended?.weapons ?? []).map((w: any) => [w.referenceId, w.values?.uniqueWeaponKills?.basic?.value ?? 0]));
          const u = e.player?.destinyUserInfo;
          if (u?.membershipId) { const k = `${u.membershipType}:${u.membershipId}`; fresh.push(k); if (e.characterId) m.i.push(`${k}:${e.characterId}`); }
        }
        store[id] = m;
        got++;
      }, timeUp);
      progress(`Collecting ${side.toUpperCase()} matches: ${Math.min(got, need)} of ${need}...`);
      if (got - lastSave >= 40) { lastSave = got; await publish(store); }
      // spread out: the recent matches of new players from these matches
      const add = [...new Set(fresh)].filter((p) => !seen.has(p)).slice(0, 30);
      for (const p of add) { seen.add(p); met.push(p); }
      if (add.length && queue.length < 80) queue.push(...(await histories(add, SIDE_MODES[side], 8, cutoff, timeUp)));
    }
  }
  await publish(store);
  if (!stop()) await builds(store, progress, stop);
}

async function publish(store: Store) {
  await idbSet('meta:store', store);
  await idbSet('meta:result', await count(store));
}

async function count(store: Store): Promise<MetaResult> {
  const week = Date.now() - 7 * 86400e3;
  const [wish, lite] = await Promise.all([godRolls(), items()]);
  const out = { updated: Date.now(), sides: {} } as MetaResult;
  for (const side of ['pvp', 'pve'] as Side[]) {
    const ms = Object.values(store).filter((m) => m.s === side);
    let players = 0, now = 0, prev = 0;
    const w = new Map<number, { u: number; k: number; n: number; p: number }>();
    for (const m of ms) {
      const recent = m.d >= week;
      for (const pw of m.p) {
        players++;
        if (recent) now++; else prev++;
        for (const [h, k] of pw) {
          if (!h) continue;
          const c = w.get(h) ?? { u: 0, k: 0, n: 0, p: 0 };
          c.u++; c.k += k; if (recent) c.n++; else c.p++;
          w.set(h, c);
        }
      }
    }
    const top = [...w].sort((a, b) => b[1].u - a[1].u).slice(0, 40);
    const weapons: MetaWeapon[] = await Promise.all(top.map(async ([hash, c]) => {
      // no roll for this exact copy: a reissue with the same name (the list covers current versions)
      let rolls = wish[hash] ?? [];
      if (!rolls.length && lite[hash]) {
        const name = lite[hash].name;
        const other = Object.keys(wish).find((h) => lite[h as any]?.name === name && lite[h as any]?.itemType === 3);
        if (other) rolls = wish[other as any];
      }
      // the first group for this side; its first perk in each column is the list's first (top) roll
      const pick = rolls.find((r) => r.mode === side) ?? rolls.find((r) => r.mode === 'any');
      // the weapon's frame: its intrinsic socket (needs the full definition, kept after the first time)
      let frame = '';
      const d = await definition<any>('DestinyInventoryItemDefinition', hash);
      const intr = d?.sockets?.socketCategories?.find((x: any) => x.socketCategoryHash === 3956125808)?.socketIndexes?.[0];
      const fh = intr !== undefined ? d.sockets.socketEntries?.[intr]?.singleInitialItemHash : 0;
      if (fh) frame = lite[fh]?.name ?? '';
      return {
        hash, usage: players ? (100 * c.u) / players : 0, kills: c.k, trend: now && prev ? (100 * c.n) / now - (100 * c.p) / prev : null, frame,
        godRoll: (pick?.cols ?? []).map((c) => c[0]).filter((p) => lite[p]).map((p) => ({ name: lite[p].name, icon: lite[p].icon })),
      };
    }));
    out.sides[side] = { matches: ms.length, players, top: weapons };
  }
  return out;
}

// ---------------- Meta Builds: the real equipped loadouts of players from these matches ----------------

interface Loadout { side: Side; tag: string; cls: number; stats: Record<string, number>; sub: [number, number[]]; armor: Record<string, [number, number[]]>; weapons: number[] }

async function builds(store: Store, progress: (t: string) => void, stop: () => boolean) {
  const specs: string[] = [];
  for (const side of ['pvp', 'pve'] as Side[]) {
    const seen = new Set<string>();
    for (const m of Object.values(store).filter((x) => x.s === side && x.i.length).sort((a, b) => b.d - a.d))
      for (const spec of m.i) {
        const mid = spec.split(':')[1];
        if (seen.has(mid) || seen.size >= (side === 'pvp' ? 90 : 70)) continue;
        seen.add(mid);
        specs.push(`${spec}:${m.t}:${side}`);
      }
  }
  if (!specs.length) return;
  progress(`Reading ${specs.length} players' equipped loadouts...`);
  const records: Loadout[] = [];
  await pool(specs, async (sp) => {
    const [type, mid, cid, tag, side] = sp.split(':');
    const prof = await bungie<any>(`/Destiny2/${type}/Profile/${mid}/?components=200,205,305`);
    const ch = prof.characters?.data?.[cid];
    const list: any[] = prof.characterEquipment?.data?.[cid]?.items ?? [];
    if (!ch || !list.length) return;
    const sockets = prof.itemComponents?.sockets?.data ?? {};
    const stats: Record<string, number> = {};
    for (const [k, v] of Object.entries<number>(ch.stats ?? {})) if (k !== POWER_STAT) stats[k] = v;
    let sub: [number, number[]] | undefined;
    const armor: Record<string, [number, number[]]> = {};
    const weapons: number[] = [];
    for (const it of list) {
      const plugs: number[] = it.itemInstanceId ? (sockets[it.itemInstanceId]?.sockets ?? []).map((s: any) => s.plugHash).filter(Boolean) : [];
      const slot = BUCKET_SLOTS.find(([b]) => b === it.bucketHash)?.[1];
      if (it.bucketHash === SUB_BUCKET) sub = [it.itemHash, plugs];
      else if (slot) armor[slot] = [it.itemHash, plugs];
      else if (WEAPON_BUCKETS.includes(it.bucketHash)) weapons.push(it.itemHash);
    }
    if (sub) records.push({ side: side as Side, tag, cls: ch.classType, stats, sub, armor, weapons });
  }, stop);

  const [lite, t] = await Promise.all([items(), tablesOf('DestinyStatDefinition', 'DestinyEquipableItemSetDefinition', 'DestinySandboxPerkDefinition')]);
  const setOf = new Map<number, any>();
  for (const s of Object.values<any>(t.DestinyEquipableItemSetDefinition)) for (const h of s.setItems ?? []) setOf.set(h, s);
  const D = (h: number): Plug | null => (lite[h] ? { hash: h, name: lite[h].name, icon: lite[h].icon, type: lite[h].type } : null);
  const topPlug = (plugs: number[], rx: RegExp, n: number, exclude?: RegExp) => {
    const c = new Map<number, number>();
    for (const p of plugs) { const d = lite[p]; if (d?.name && rx.test(d.type) && !/^Empty|^Default/.test(d.name) && !(exclude && exclude.test(d.type))) c.set(p, (c.get(p) ?? 0) + 1); }
    return [...c].sort((a, b) => b[1] - a[1]).slice(0, n).map(([h]) => D(h)!);
  };
  const mostOf = <T,>(list: T[]) => { const c = new Map<T, number>(); for (const x of list) c.set(x, (c.get(x) ?? 0) + 1); return [...c].sort((a, b) => b[1] - a[1]).map(([h]) => h); };
  const CLS = ['Titan', 'Hunter', 'Warlock'];
  const out: Build[] = [];
  for (const side of ['pvp', 'pve'] as Side[])
    for (const cls of [0, 1, 2]) {
      const cr = records.filter((r) => r.side === side && r.cls === cls);
      if (!cr.length) continue;
      // same class + subclass + exotic armour = one build
      const groups = new Map<string, Loadout[]>();
      for (const r of cr) {
        const ex = BUCKET_SLOTS.map(([, s]) => r.armor[s]?.[0]).find((h) => h && lite[h]?.tier === 6);
        const k = `${r.sub[0]}|${ex ?? ''}`;
        groups.set(k, [...(groups.get(k) ?? []), r]);
      }
      for (const m of [...groups.values()].sort((a, b) => b.length - a.length).slice(0, 3)) {
        const subPlugs = m.flatMap((r) => r.sub[1]);
        const armor: Plug[] = [];
        for (const [, slot] of BUCKET_SLOTS) {
          const h = mostOf(m.filter((r) => r.armor[slot]).map((r) => r.armor[slot][0]))[0];
          const d = h ? D(h) : null;
          if (d) armor.push({ ...d, slot, exotic: lite[h].tier === 6 });
        }
        const sets: Build['sets'] = [];
        const bySet = new Map<any, number>();
        for (const a of armor) { const s = setOf.get(a.hash); if (s) bySet.set(s, (bySet.get(s) ?? 0) + 1); }
        for (const [s, pieces] of bySet) {
          const bonuses = (s.setPerks ?? []).filter((sp: any) => sp.requiredSetCount <= pieces).map((sp: any) => {
            const pk = t.DestinySandboxPerkDefinition[sp.sandboxPerkHash]?.displayProperties;
            return pk ? { count: sp.requiredSetCount, name: pk.name, desc: pk.description } : null;
          }).filter(Boolean);
          sets.push({ name: s.displayProperties?.name ?? '', pieces, bonuses });
        }
        const statKeys = [...new Set(m.flatMap((r) => Object.keys(r.stats)))];
        const stats = statKeys.map((k) => {
          const vals = m.map((r) => r.stats[k]).filter((v) => v !== undefined);
          return { name: t.DestinyStatDefinition[k]?.displayProperties?.name ?? '', value: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) };
        }).filter((s) => s.name).sort((a, b) => b.value - a.value);
        const tags = mostOf(m.map((r) => r.tag)).slice(0, 2);
        out.push({
          side, cls: CLS[cls], purpose: tags.join(' / '), count: m.length, share: Math.round((100 * m.length) / cr.length), subclass: D(m[0].sub[0]),
          super: topPlug(subPlugs, /Super/, 1), abilities: topPlug(subPlugs, /Grenade|Melee|Class Ability|Movement|Jump/, 4), aspects: topPlug(subPlugs, /Aspect/, 2),
          fragments: topPlug(subPlugs, /Fragment/, 6), armor, sets, mods: topPlug(m.flatMap((r) => Object.values(r.armor).flatMap((a) => a[1])), /Mod/, 8, /Ornament|Shader/),
          stats, weapons: mostOf(m.flatMap((r) => r.weapons)).slice(0, 4).map(D).filter((x): x is Plug => !!x),
        });
      }
    }
  await idbSet('meta:builds', { updated: Date.now(), players: records.length, builds: out } satisfies BuildsResult);
}
